import PageHeader from "@/components/PageHeader";
import ProfileForm from "@/components/ProfileForm";
import CharacterSummary from "@/components/CharacterSummary";
import AiAssistant from "@/components/AiAssistant";
import api from "@/lib/api";

// Etapp 2: Självskattad karaktärsprofil i tio dimensioner (D1–D10).
// Varje dimension: en skattning (1–5) + ett konkret exempel. Inga personlighetstest.
const dim = (id, title, ratingLabel, exampleLabel, examplePlaceholder) => ({
  title,
  description: "Detta är ingen bedömning eller test – beskriv hur du själv upplever att du fungerar i arbetslivet.",
  fields: [
    { name: `${id}_rating`, label: ratingLabel, type: "likert" },
    { name: `${id}_example`, label: exampleLabel, type: "textarea", full: true, rows: 3, placeholder: examplePlaceholder,
      help: "Ett konkret exempel gör din profil trovärdig. AI använder bara det du själv skriver." },
  ],
});

const GROUPS = [
  dim("d1", "D1 · Ansvar och pålitlighet",
    "Jag tar ansvar och håller vad jag lovar",
    "Exempel på när du tog ansvar eller höll ett viktigt löfte",
    "T.ex. \u201eJag tog över ansvaret för…\u201c"),
  dim("d2", "D2 · Samarbete och bemötande",
    "Jag samarbetar bra och bemöter andra med respekt",
    "Exempel på ett bra samarbete eller bemötande",
    "T.ex. \u201eI ett teamprojekt bidrog jag genom att…\u201c"),
  dim("d3", "D3 · Struktur och kvalitet",
    "Jag arbetar strukturerat och levererar med kvalitet",
    "Exempel på hur du håller ordning och kvalitet",
    "T.ex. \u201eJag skapade en rutin som…\u201c"),
  dim("d4", "D4 · Initiativ och problemlösning",
    "Jag tar initiativ och löser problem som dyker upp",
    "Exempel på ett initiativ eller problem du löste",
    "T.ex. \u201eNär ett problem uppstod tog jag initiativet att…\u201c"),
  dim("d5", "D5 · Anpassning och press",
    "Jag anpassar mig och fungerar bra under press",
    "Exempel på när du hanterade förändring eller press",
    "T.ex. \u201eUnder en hektisk period…\u201c"),
  dim("d6", "D6 · Lärande och återkoppling",
    "Jag lär mig nytt och tar till mig återkoppling",
    "Exempel på något du lärt dig eller feedback du tagit till dig",
    "T.ex. \u201eJag fick feedback om… och gjorde…\u201c"),
  dim("d7", "D7 · Motivation och arbetsmiljö",
    "Jag vet vad som motiverar mig i arbetet",
    "Vad motiverar dig, och i vilken arbetsmiljö trivs du bäst?",
    "T.ex. \u201eJag motiveras av… och trivs bäst när…\u201c"),
  {
    title: "D8 · Social stil och grupproll",
    description: "Hur fungerar du socialt och vilken roll tar du ofta i en grupp?",
    fields: [
      { name: "d8_group_role", label: "Din vanligaste roll i en grupp", type: "select",
        options: ["Drivande / ledare", "Samordnare", "Idéspruta", "Genomförare / doer", "Stödjande / lagspelare", "Analytiker"] },
      { name: "d8_example", label: "Kort om hur du fungerar socialt på jobbet", type: "textarea", full: true, rows: 3,
        placeholder: "T.ex. \u201eJag är den som ofta ser till att alla kommer till tals…\u201c" },
    ],
  },
  {
    title: "D9 · Intressen och hjärtefrågor",
    description: "Vad engagerar dig – på och utanför jobbet? (Frivilligt)",
    fields: [
      { name: "d9_interests", label: "Intressen och hjärtefrågor", type: "tags", full: true, placeholder: "Skriv och tryck Enter" },
      { name: "d9_example", label: "Berätta gärna kort varför något av detta betyder mycket för dig", type: "textarea", full: true, rows: 2 },
    ],
  },
  {
    title: "D10 · Utveckling och framtid",
    description: "Din egen bild av var du är på väg.",
    fields: [
      { name: "d10_strengths", label: "Dina främsta styrkor", type: "tags", full: true, placeholder: "Skriv och tryck Enter" },
      { name: "d10_development_areas", label: "Möjliga utvecklingsområden", type: "tags", full: true, placeholder: "Skriv och tryck Enter" },
      { name: "d10_example", label: "Vad vill du utveckla framåt?", type: "textarea", full: true, rows: 3 },
    ],
  },
  {
    title: "Så beskriver du dig själv",
    description: "Med dina egna ord.",
    fields: [
      { name: "self_image", label: "Hur skulle du beskriva dig själv i arbetslivet?", type: "textarea", full: true, rows: 3 },
    ],
  },
];

export default function Karaktar() {
  return (
    <div>
      <PageHeader
        title="Karaktär och styrkor"
        description="Din självskattade karaktärsprofil i tio dimensioner (D1–D10). Svara ärligt och ge gärna konkreta exempel – det hjälper AI att lyfta dina verkliga styrkor utan att hitta på något."
      />
      <div className="flex justify-end -mt-2 mb-4">
        <AiAssistant
          triggerLabel="Fråga AI om hjälp"
          triggerTestId="karaktar-ai-trigger"
          title="AI-hjälp med din karaktär"
          intro="Jag hjälper dig hitta ord för dina styrkor och ge konkreta exempel – utifrån det du själv berättar."
          suggestions={[
            "Hjälp mig beskriva min samarbetsförmåga med ett exempel",
            "Vad kan jag skriva om jag är strukturerad?",
            "Hur sätter jag ord på mina drivkrafter?",
          ]}
          loadHistory={async () => (await api.get("/assistant/advice/karaktar")).data}
          send={async (message) => (await api.post("/assistant/advice", { topic: "karaktar", message })).data}
        />
      </div>
      <ProfileForm groups={GROUPS} endpoint="/profile/character" loadKey="character" />
      <div className="mt-8">
        <CharacterSummary />
      </div>
    </div>
  );
}
